<div class="flex h-full min-h-0 w-full flex-col overflow-y-auto scrollbar-transparent">
  <div class="shrink-0 p-4">
    <div class="space-y-4">
      <div>
        <h4 class="text-sm font-semibold text-primary">Register creator</h4>
        <p class="mt-1 text-xs text-secondary">
          Create or repair a Shout creator record for a Firebase user who failed app signup.
          Submits <code class="text-primary">POST /creators/admin/register</code>.
        </p>
      </div>

      <form
        class="grid w-full max-w-3xl gap-4 sm:grid-cols-2"
        (submit)="$event.preventDefault(); runRegister()"
        [attr.aria-disabled]="actionLoading()"
      >
        <fieldset class="contents" [disabled]="actionLoading()">
          <div class="space-y-1 sm:col-span-2">
            <label for="reg-firebase-uid" class="block text-sm font-medium text-primary">Firebase UID</label>
            <div class="flex flex-col gap-2 sm:flex-row">
              <input
                id="reg-firebase-uid"
                type="text"
                autocomplete="off"
                class="block min-w-0 flex-1 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
                [formField]="registrationForm.blazeFirebaseUid"
              />
              <button
                type="button"
                class="shrink-0 rounded-lg border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-primary shadow-sm disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-950"
                (click)="runLookup()"
                [disabled]="lookupLoading() || actionLoading()"
              >
                @if (lookupLoading()) {
                  Looking up…
                } @else {
                  Lookup UID
                }
              </button>
            </div>
          </div>

          @if (lookupResult(); as lookup) {
          <div class="sm:col-span-2 flex flex-wrap gap-2">
            <span
              class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              [class]="lookup.exists ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200' : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200'"
            >
              Firebase {{ lookup.exists ? 'found' : 'not found' }}
            </span>
            <span
              class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              [class]="lookup.creatorExists ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200' : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200'"
            >
              Creator row {{ lookup.creatorExists ? 'exists' : 'missing' }}
            </span>
            @if (lookup.displayName) {
            <span class="inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200">
              {{ lookup.displayName }}
            </span>
            }
          </div>
          }

          @if (lookupError()) {
          <p class="sm:col-span-2 m-0 text-sm text-red-600 dark:text-red-400">{{ lookupError() }}</p>
          }

          <div class="space-y-1 sm:col-span-2">
            <label for="reg-email" class="block text-sm font-medium text-primary">Email</label>
            <input
              id="reg-email"
              type="email"
              autocomplete="off"
              class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
              [formField]="registrationForm.email"
            />
          </div>

          <div class="space-y-1">
            <label for="reg-first-name" class="block text-sm font-medium text-primary">First name</label>
            <input
              id="reg-first-name"
              type="text"
              autocomplete="off"
              class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
              [formField]="registrationForm.firstName"
            />
          </div>

          <div class="space-y-1">
            <label for="reg-last-name" class="block text-sm font-medium text-primary">Last name</label>
            <input
              id="reg-last-name"
              type="text"
              autocomplete="off"
              class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
              [formField]="registrationForm.lastName"
            />
          </div>

          <div class="space-y-1">
            <label for="reg-phone-country" class="block text-sm font-medium text-primary">Phone country</label>
            <select
              id="reg-phone-country"
              class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
              [value]="registrationModel().phoneCountry"
              (change)="onPhoneCountryChange($any($event.target).value)"
            >
              @for (preset of countryPresets; track preset.countryCode) {
              <option [value]="preset.countryCode">{{ preset.label }}</option>
              }
            </select>
          </div>

          <div class="space-y-1">
            <label for="reg-phone-local" class="block text-sm font-medium text-primary">
              Phone number (without {{ selectedPhonePreset().prefix }})
            </label>
            <input
              id="reg-phone-local"
              type="tel"
              inputmode="numeric"
              autocomplete="off"
              class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
              [formField]="registrationForm.phoneLocal"
            />
            <p class="m-0 text-xs text-secondary">Do not include a leading 0.</p>
          </div>

          <div class="space-y-1 sm:col-span-2">
            <p class="m-0 text-sm font-medium text-primary">Payout currency</p>
            <div class="flex flex-wrap gap-2" role="group" aria-label="Payout currency">
              @for (option of currencyOptions; track option.code) {
              <button
                type="button"
                class="rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
                [class]="registrationModel().currencyCode === option.code
                  ? 'border-primary bg-primary text-white'
                  : 'border-zinc-200 bg-white text-primary dark:border-zinc-700 dark:bg-zinc-950'"
                (click)="onCurrencyChange(option.code)"
              >
                {{ option.label }}
              </button>
              }
            </div>
          </div>

          <div class="sm:col-span-2 flex flex-wrap items-center gap-3">
            <button
              type="submit"
              class="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              [disabled]="submitDisabled()"
            >
              @if (actionLoading()) {
                Saving…
              } @else {
                {{ submitButtonLabel() }}
              }
            </button>

            @if (lastRegisteredUid(); as registeredUid) {
            <a
              class="text-sm font-medium text-primary underline underline-offset-2"
              [routerLink]="['/admin-options/user-profile', registeredUid]"
            >
              Open creator profile
            </a>
            @if (lastActionCreated() === false) {
            <p class="m-0 text-xs text-secondary">Existing record was updated.</p>
            }
            }
          </div>
        </fieldset>
      </form>
    </div>
  </div>
</div>
